import type { ShareLinkConfig } from '@workspace/seedforge-features/share'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import { Info } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'
import { DeckReadingChart } from './deck-reading-chart'
import type { FounderProfileAnalyticsData } from './types'

const TOOLTIP_STYLE = {
  background: '#1a1a1a',
  border: '1px solid #333',
  borderRadius: 8,
  fontSize: 11,
}

const TILE_ORDER: { label: string; tip: string }[] = [
  {
    label: 'Visits',
    tip: 'Every human open of your profile in the period. Bots, link scanners, and your own visits are excluded.',
  },
  {
    label: 'People',
    tip: 'Distinct viewers, counted by device and identity. One person opening five times counts once here.',
  },
  {
    label: 'Avg. time on profile',
    tip: 'Average active reading time per visit. Idle tabs do not count.',
  },
  {
    label: 'Chat questions asked',
    tip: 'How many questions investors have asked Ask the Profile. A question is a stronger read signal than a section expand.',
  },
  {
    label: 'Came back for a second look',
    tip: 'People with more than one visit. A returning investor is the strongest passive interest signal.',
  },
]

function InfoDot({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="size-3 shrink-0 text-muted-foreground/60" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64">
        {text}
      </TooltipContent>
    </Tooltip>
  )
}

// Mirrors both the profile page's own render order
// (profile-editable-view.tsx: header, deck, chat, description, team, pitch
// recording, documents) and the Visitors log's "Read" chips one-for-one,
// rather than lumping header/description/team into one unlabeled bucket.
const SOURCE_SEGMENTS = [
  { key: 'header', label: 'Header', className: 'bg-blue-400' },
  { key: 'pitchDeck', label: 'Deck', className: 'bg-[#f97316]' },
  { key: 'chat', label: 'Chat', className: 'bg-sky-500' },
  { key: 'description', label: 'Description', className: 'bg-emerald-500' },
  { key: 'team', label: 'Team', className: 'bg-violet-500' },
  { key: 'pitchVideo', label: 'Pitch recording', className: 'bg-[#b45309]' },
  { key: 'documents', label: 'Documents', className: 'bg-muted-foreground/30' },
] as const

/** Zone 2 of "Share your Profile": numbers tiles, visits chart, time split. */
export function ShareAnalyticsPanel({
  analytics,
  isLoading,
  link,
}: {
  analytics: FounderProfileAnalyticsData | undefined
  isLoading: boolean
  link: ShareLinkConfig | undefined
}) {
  if (isLoading || !analytics) return null

  const hasVisits = analytics.visitors.length > 0
  if (!hasVisits) return <ShareAnalyticsEmptyState link={link} />

  const statByLabel = new Map(analytics.stats.map((s) => [s.label, s.value]))
  const filter = analytics.analyticsFilter
  const breakdown = analytics.sourceTimeBreakdown
  const hasDeckReading = analytics.deckReading.some((d) => d.pages.length > 0)

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Last 30 days
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {TILE_ORDER.map((tile) => (
            <div key={tile.label} className="rounded-xl border border-border bg-card/30 px-4 py-4">
              <p className="text-2xl font-bold tabular-nums text-foreground">
                {statByLabel.get(tile.label) ?? '0'}
              </p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                {tile.label}
                <InfoDot text={tile.tip} />
              </p>
            </div>
          ))}
        </div>
        {filter.excludedDataRoomCount > 0 && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground/70">
            <span className="text-emerald-400">✓</span> {filter.excludedDataRoomCount} automated
            link scanner
            {filter.excludedDataRoomCount === 1 ? '' : 's'} filtered out. Your own visits are never
            counted.
          </p>
        )}
        {filter.excludedDataRoomCount === 0 && (
          <p className="mt-2 text-xs text-muted-foreground/70">
            Your own visits are never counted.
          </p>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card/30 px-4 py-3.5">
          <div className="mb-2 flex items-baseline justify-between">
            <p className="flex items-center gap-1.5 text-[13.5px] font-semibold text-foreground">
              Visits per day
              <InfoDot text="Human visits only, per calendar day." />
            </p>
            <span className="text-[11px] text-muted-foreground">last 30 days</span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart
              data={analytics.visitorActivity}
              margin={{ top: 4, right: 0, bottom: 0, left: -24 }}
              barCategoryGap="15%"
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 8, fill: '#666' }}
                tickLine={false}
                axisLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#555' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Bar dataKey="views" fill="#f97316" radius={[2, 2, 0, 0]} />
              <RechartsTooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                separator=""
                formatter={(value) => [`${value} visits`, '']}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {hasDeckReading ? (
          <DeckReadingChart deckReading={analytics.deckReading} />
        ) : (
          <div className="rounded-xl border border-border bg-card/30 px-4 py-3.5">
            <p className="flex items-center gap-1.5 text-[13.5px] font-semibold text-foreground">
              How far investors read your deck
              <InfoDot text="Average seconds spent on each deck page, and where readers stop." />
            </p>
            <p className="mt-3 text-xs text-muted-foreground">No deck reading recorded yet.</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card/30 px-4 py-3.5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <p className="flex items-center gap-1.5 whitespace-nowrap text-[13.5px] font-semibold text-foreground">
            Where visitors spend their time
            <InfoDot text="Active seconds across all visits, split by what was on screen." />
          </p>
          {breakdown.totalSeconds === 0 ? (
            <p className="text-xs text-muted-foreground">No reading time recorded yet.</p>
          ) : (
            <TimeSplitBar breakdown={breakdown} />
          )}
        </div>
      </div>
    </div>
  )
}

function TimeSplitBar({
  breakdown,
}: {
  breakdown: FounderProfileAnalyticsData['sourceTimeBreakdown']
}) {
  const total = breakdown.totalSeconds
  const segments = SOURCE_SEGMENTS.map((s) => {
    const seconds = breakdown[s.key]
    const pct = Math.round((seconds / total) * 100)
    return { ...s, seconds, pct }
  }).filter((s) => s.pct > 0)

  return (
    <div className="flex flex-1 flex-col gap-2.5 lg:flex-row lg:items-center lg:gap-6">
      <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-card2">
        {segments.map((s) => (
          <div key={s.key} className={cn('h-full', s.className)} style={{ width: `${s.pct}%` }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3 whitespace-nowrap text-[11px] text-muted-foreground">
        {segments.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span className={cn('size-2.5 rounded-sm', s.className)} />
            {s.label} {s.pct}%
          </span>
        ))}
      </div>
    </div>
  )
}

/** Mockup's "State: before the first visit" — replaces the whole zone. */
export function ShareAnalyticsEmptyState({ link }: { link: ShareLinkConfig | undefined }) {
  const { copied, copy } = useCopyToClipboard()

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border py-10 text-center">
      <p className="text-[17px] font-semibold text-foreground">
        Share your profile and watch who reads what
      </p>
      <p className="max-w-[46ch] text-[13px] text-muted-foreground">
        Send your link to any investor. The moment someone opens it, this page fills with who they
        are, how long they stayed, and how far into your deck they read.
      </p>
      {link && (
        <CtaButton onClick={() => copy(`${link.domain}/${link.slug}`)}>
          {copied ? 'Copied' : 'Copy your link'}
        </CtaButton>
      )}
      <div className="mt-2 grid w-full max-w-[520px] grid-cols-3 gap-2.5 opacity-40">
        {['Visitors', 'Time on profile', 'Deck pages read'].map((label) => (
          <div
            key={label}
            className="rounded-lg border border-dashed border-border px-3 py-3 text-[11px] text-muted-foreground"
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  )
}
